import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { BRAND, GRADIENT } from '../theme';

// ProgressBar
//
// Linear progress with explicit phase labels above. Phases:
//   Approve · Burn · Relay · Mint · Done
// At a given currentIndex (0-based) it highlights the active step and
// chequers off prior ones. On failure (failedIndex set) the failed step
// pulses red.

const PHASES = ['Approve', 'Burn', 'Relay', 'Mint', 'Done'];

export default function ProgressBar({ currentIndex = 0, failedIndex = null }) {
  const safeIdx = Math.max(0, Math.min(currentIndex, PHASES.length - 1));
  const pct = (safeIdx / (PHASES.length - 1)) * 100;
  const isFailed = failedIndex != null;
  const isDone = safeIdx === PHASES.length - 1 && !isFailed;

  return (
    <Box sx={{ width: '100%' }} data-testid="progress-bar">
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.25 }}>
        {PHASES.map((label, i) => {
          const reached = i <= safeIdx;
          const isFail = isFailed && i === failedIndex;
          const isActive = i === safeIdx && !isFailed && !isDone;
          return (
            <Stack
              key={label}
              alignItems="center"
              spacing={0.5}
              sx={{ flex: 1, position: 'relative' }}
              data-testid={`phase-${label.toLowerCase()}`}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  border: '1.5px solid',
                  borderColor: isFail
                    ? BRAND.error
                    : reached
                      ? BRAND.violet
                      : BRAND.border,
                  background: isFail
                    ? `${BRAND.error}22`
                    : reached
                      ? GRADIENT
                      : 'transparent',
                  color: reached ? '#fff' : BRAND.textSecondary,
                  animation: isActive ? 'phasePulse 1.6s ease-in-out infinite' : 'none',
                  '@keyframes phasePulse': {
                    '0%, 100%': { boxShadow: `0 0 0 0 ${BRAND.violet}88` },
                    '50%':      { boxShadow: `0 0 0 8px ${BRAND.violet}00` },
                  },
                  transition: 'all 200ms ease',
                }}
              >
                {isFail ? <CloseIcon sx={{ fontSize: 16 }} /> : reached ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: isFail
                    ? BRAND.error
                    : reached
                      ? BRAND.textPrimary
                      : BRAND.textSecondary,
                  fontWeight: isActive || isFail ? 700 : 500,
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={isFailed ? 'error' : isDone ? 'success' : 'primary'}
        sx={{
          height: 6,
          '& .MuiLinearProgress-bar': {
            background: isDone ? BRAND.success : isFailed ? BRAND.error : GRADIENT,
          },
        }}
      />
    </Box>
  );
}
